import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Dropdown, Tag, Switch, message, Input, InputNumber, Upload, Spin, Select, DatePicker, Divider } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, MoreOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import {
  getCampaignsApi,
  createCampaignApi,
  updateCampaignApi,
  deleteCampaignApi,
  uploadImageApi,
  getProductsApi,
  getCategoriesApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";

const ACCENT = "#534ab7";

// Small curated offset list — not the full IANA database, just the handful
// Kidodom actually schedules campaigns in. Stored as a plain "+05:30"-style
// offset string, combined with the date/time picker's numbers into one ISO
// instant (e.g. "2026-09-04T00:00:00+05:30") before it ever reaches the
// backend, so the server never has to guess what timezone an admin meant.
const TIMEZONES = [
  { label: "IST — India (UTC+05:30)", value: "+05:30" },
  { label: "UTC (UTC+00:00)", value: "+00:00" },
  { label: "GST — Gulf (UTC+04:00)", value: "+04:00" },
  { label: "SGT — Singapore (UTC+08:00)", value: "+08:00" },
  { label: "BST — UK (UTC+01:00)", value: "+01:00" },
  { label: "EST — US Eastern (UTC-05:00)", value: "-05:00" },
  { label: "PST — US Pacific (UTC-08:00)", value: "-08:00" },
];

const STATUS_COLOR = { DRAFT: "default", SCHEDULED: "gold", ACTIVE: "success", PAUSED: "orange", ENDED: "default" };

const RESTRICTION_LABEL = {
  NONE: "No restriction",
  SELECTED_PRODUCTS: "Selected products",
  SELECTED_CATEGORIES: "Selected categories",
  ALL_PRODUCTS: "All products",
};
const RESTRICTION_COLOR = { NONE: "default", SELECTED_PRODUCTS: "gold", SELECTED_CATEGORIES: "gold", ALL_PRODUCTS: "red" };

// Splits a stored "...T...±HH:mm" instant into the wall-clock dayjs value
// (for the DatePicker) and the raw offset string (for the Timezone select) —
// deliberately NOT using dayjs's own timezone conversion, which would
// re-render the numbers in the admin browser's local zone instead of the
// zone the campaign was actually configured in.
function splitInstant(iso) {
  if (!iso) return { dt: null, tz: "+05:30" };
  const m = /^(.*)([+-]\d{2}:\d{2})$/.exec(iso);
  if (!m) return { dt: dayjs(iso), tz: "+05:30" };
  return { dt: dayjs(m[1]), tz: m[2] };
}
function joinInstant(dt, tz) {
  if (!dt) return null;
  return `${dt.format("YYYY-MM-DDTHH:mm:ss")}${tz || "+05:30"}`;
}

function ImageField({ label, value, onChange, folder, hint }) {
  const [uploading, setUploading] = useState(false);
  const handleUpload = (file) => {
    (async () => {
      setUploading(true);
      try {
        const res = await uploadImageApi(file, folder);
        const url = res?.data?.data;
        if (url) onChange(url);
      } catch {
        message.error("Image upload failed");
      } finally {
        setUploading(false);
      }
    })();
    return false;
  };
  return (
    <Form.Item label={label} tooltip={hint}>
      {value ? (
        <div style={{ position: "relative", display: "inline-block" }}>
          <img src={value} alt="" style={{ width: 160, height: 90, objectFit: "cover", borderRadius: 10, border: "1px solid #eee" }} />
          <Button danger size="small" icon={<DeleteOutlined />} style={{ position: "absolute", top: 4, right: 4, padding: "0 6px", minWidth: 0 }} onClick={() => onChange("")} />
        </div>
      ) : (
        <Upload accept="image/*" beforeUpload={handleUpload} showUploadList={false} disabled={uploading}>
          <Button icon={<UploadOutlined />} loading={uploading}>Upload</Button>
        </Upload>
      )}
    </Form.Item>
  );
}

export default function CampaignManager() {
  const [campaigns, setCampaigns] = useState([]);
  const [products,  setProducts]  = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [open,      setOpen]      = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [bannerUrl,       setBannerUrl]       = useState("");
  const [mobileBannerUrl, setMobileBannerUrl] = useState("");
  const [bgUrl,           setBgUrl]           = useState("");
  const [startTz, setStartTz] = useState("+05:30");
  const [endTz,   setEndTz]   = useState("+05:30");
  const [restrictStartTz, setRestrictStartTz] = useState("+05:30");
  const [unlockTz,        setUnlockTz]        = useState("+05:30");
  const [restriction, setRestriction] = useState("NONE"); // mirrors the form field, so the product/category pickers can show/hide
  const [form] = Form.useForm();

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await getCampaignsApi();
      setCampaigns(res.data.data || []);
    } catch {
      message.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCampaigns();
    // Products/categories only ever needed once the picker for either is
    // actually shown — but the list is small enough (this is an admin
    // console, not the storefront) to just fetch once up front and reuse.
    getProductsApi({ page: 0, size: 500 }).then((res) => {
      const list = res?.data?.data ?? res?.data ?? [];
      setProducts(Array.isArray(list) ? list : (list.items || list.products || []));
    }).catch(() => {});
    getCategoriesApi().then((res) => {
      const list = res?.data?.data ?? res?.data ?? [];
      setCategories(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, []);

  const productOptions = products.map((p) => ({ label: p.name, value: p.id }));
  const categoryOptions = categories.map((c) => ({ label: c.name, value: c.id }));

  const resetImages = () => { setBannerUrl(""); setMobileBannerUrl(""); setBgUrl(""); };

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    resetImages();
    setStartTz("+05:30"); setEndTz("+05:30"); setRestrictStartTz("+05:30"); setUnlockTz("+05:30");
    setRestriction("NONE");
    form.setFieldsValue({
      countdownEnabled: true, countdownType: "AUTO", status: "DRAFT",
      displayPriority: 0, enabled: true, purchaseRestriction: "NONE", theme: "festive",
    });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setBannerUrl(c.bannerImageUrl || "");
    setMobileBannerUrl(c.mobileBannerImageUrl || "");
    setBgUrl(c.backgroundImageUrl || "");
    const start = splitInstant(c.startAt);
    const end   = splitInstant(c.endAt);
    const restrictStart = splitInstant(c.restrictionStartAt);
    const unlock         = splitInstant(c.purchaseUnlockAt);
    setStartTz(start.tz); setEndTz(end.tz);
    setRestrictStartTz(restrictStart.tz); setUnlockTz(unlock.tz);
    setRestriction(c.purchaseRestriction || "NONE");
    form.setFieldsValue({
      name: c.name, title: c.title, subtitle: c.subtitle,
      startAt: start.dt, endAt: end.dt,
      ctaText: c.ctaText, ctaAction: c.ctaAction,
      countdownEnabled: c.countdownEnabled, countdownType: c.countdownType || "AUTO",
      status: c.status, displayPriority: c.displayPriority ?? 0,
      enabled: c.enabled, theme: c.theme || "festive",
      purchaseRestriction: c.purchaseRestriction || "NONE",
      restrictionStartAt: restrictStart.dt,
      purchaseUnlockAt: unlock.dt,
      restrictedProductIds: c.restrictedProductIds || [],
      restrictedCategoryIds: c.restrictedCategoryIds || [],
      restrictionMessage: c.restrictionMessage,
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    const v = await form.validateFields();
    if (!v.startAt) { message.error("Start date/time is required"); return; }
    if (v.purchaseRestriction === "SELECTED_PRODUCTS" && !(v.restrictedProductIds || []).length) {
      message.error("Select at least one product to restrict"); return;
    }
    if (v.purchaseRestriction === "SELECTED_CATEGORIES" && !(v.restrictedCategoryIds || []).length) {
      message.error("Select at least one category to restrict"); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: v.name, title: v.title, subtitle: v.subtitle,
        startAt: joinInstant(v.startAt, startTz),
        endAt:   v.endAt ? joinInstant(v.endAt, endTz) : null,
        bannerImageUrl: bannerUrl || null,
        mobileBannerImageUrl: mobileBannerUrl || null,
        backgroundImageUrl: bgUrl || null,
        ctaText: v.ctaText, ctaAction: v.ctaAction,
        countdownEnabled: v.countdownEnabled, countdownType: v.countdownType,
        status: v.status, displayPriority: v.displayPriority,
        enabled: v.enabled, theme: v.theme,
        purchaseRestriction: v.purchaseRestriction,
        restrictionStartAt: v.restrictionStartAt ? joinInstant(v.restrictionStartAt, restrictStartTz) : null,
        purchaseUnlockAt:   v.purchaseUnlockAt ? joinInstant(v.purchaseUnlockAt, unlockTz) : null,
        restrictedProductIds: v.purchaseRestriction === "SELECTED_PRODUCTS" ? (v.restrictedProductIds || []) : [],
        restrictedCategoryIds: v.purchaseRestriction === "SELECTED_CATEGORIES" ? (v.restrictedCategoryIds || []) : [],
        restrictionMessage: v.restrictionMessage,
      };
      if (editing) {
        await updateCampaignApi(editing.id, payload);
        message.success("Campaign updated");
      } else {
        await createCampaignApi(payload);
        message.success("Campaign created");
      }
      setOpen(false);
      fetchCampaigns();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (c) => {
    Modal.confirm({
      title: `Delete "${c.name}"?`,
      content: c.purchaseRestriction && c.purchaseRestriction !== "NONE"
        ? "This campaign is currently restricting some products from purchase — deleting it will immediately re-open them if nothing else restricts them."
        : "This removes it from the mobile app immediately.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteCampaignApi(c.id);
          message.success("Campaign deleted");
          fetchCampaigns();
        } catch {
          message.error("Failed to delete campaign");
        }
      },
    });
  };

  const quickStatus = async (c, status) => {
    try {
      await updateCampaignApi(c.id, { ...c, status });
      message.success(`"${c.name}" set to ${status}`);
      fetchCampaigns();
    } catch {
      message.error("Failed to update status");
    }
  };

  const columns = [
    {
      title: "Campaign",
      dataIndex: "name",
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600, color: "#111827" }}>{v}</div>
          <div style={{ fontSize: 11, color: "#9CA3AF" }}>{r.title}</div>
        </div>
      ),
    },
    {
      title: "Window",
      width: 220,
      render: (_, r) => (
        <div style={{ fontSize: 12, color: "#6B7280" }}>
          <div>{r.startAt ? dayjs(r.startAt).format("DD MMM YYYY, HH:mm") : "—"}</div>
          <div>{r.endAt ? `→ ${dayjs(r.endAt).format("DD MMM YYYY, HH:mm")}` : "→ open-ended"}</div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 110,
      align: "center",
      render: (v) => <Tag color={STATUS_COLOR[v] || "default"}>{v}</Tag>,
    },
    {
      title: "Priority",
      dataIndex: "displayPriority",
      width: 80,
      align: "center",
    },
    {
      title: "Purchase Restriction",
      dataIndex: "purchaseRestriction",
      width: 150,
      align: "center",
      render: (v, r) => {
        const type = v || "NONE";
        const count = type === "SELECTED_PRODUCTS" ? (r.restrictedProductIds || []).length
          : type === "SELECTED_CATEGORIES" ? (r.restrictedCategoryIds || []).length : null;
        return (
          <Tag color={RESTRICTION_COLOR[type] || "default"}>
            {RESTRICTION_LABEL[type] || type}{count != null ? ` (${count})` : ""}
          </Tag>
        );
      },
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      width: 90,
      align: "center",
      render: (v) => <Tag color={v ? "success" : "default"}>{v ? "On" : "Off"}</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              { type: "divider" },
              { key: "pause", label: "Pause (force-hide)", onClick: () => quickStatus(r, "PAUSED"), disabled: r.status === "PAUSED" },
              { key: "resume", label: "Resume (auto-schedule)", onClick: () => quickStatus(r, "SCHEDULED"), disabled: r.status !== "PAUSED" },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  const card = { background: "#fff", borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", padding: 20, marginTop: 16 };

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div className="section-title">🚀 Launch &amp; Campaign Manager</div>
          <div style={{ fontSize: 12, color: "#999", maxWidth: 640 }}>
            One reusable system for the Sept 4 launch countdown and any future sale (Diwali, Christmas, a flash sale, anything) — create as many as you like. Only one shows on the app at a time per the Priority column when more than one is live. A campaign with "Blocks Purchase" on stops checkout backend-side (not just hides a button) for as long as it's Scheduled.
          </div>
        </div>
        <Button type="primary" icon={<PlusOutlined />} style={{ background: ACCENT, border: "none", borderRadius: 8 }} onClick={openAdd}>
          New Campaign
        </Button>
      </div>

      <AppTable columns={columns} data={campaigns} loading={loading} pagination={{ pageSize: 8 }} />

      <Modal
        title={editing ? `Edit — ${editing.name}` : "New Campaign"}
        open={open}
        width={640}
        okText={editing ? "Save Changes" : "Create Campaign"}
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading: saving }}
        onOk={handleSubmit}
        onCancel={() => { setOpen(false); setEditing(null); form.resetFields(); resetImages(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <AppInput name="name" label="Campaign Name (internal)" rules={[{ required: true, message: "Required" }]} placeholder="e.g. Kidodom Launch, Diwali Sale 2026" />
          <AppInput name="title" label="Title (shown to shoppers)" rules={[{ required: true, message: "Required" }]} placeholder="e.g. Something BIG is Coming! 🎉" />
          <AppInput name="subtitle" label="Subtitle" placeholder="e.g. Get ready for amazing deals for your little ones" />

          <Divider style={{ margin: "8px 0 16px" }}>Schedule</Divider>
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="startAt" label="Start Date &amp; Time" rules={[{ required: true, message: "Required" }]} style={{ flex: 1 }}>
              <DatePicker showTime style={{ width: "100%" }} format="DD MMM YYYY, HH:mm" />
            </Form.Item>
            <Form.Item label="Timezone" style={{ width: 190 }}>
              <Select value={startTz} onChange={setStartTz} options={TIMEZONES} />
            </Form.Item>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="endAt" label="End Date &amp; Time (optional — blank = open-ended)" style={{ flex: 1 }}>
              <DatePicker showTime style={{ width: "100%" }} format="DD MMM YYYY, HH:mm" />
            </Form.Item>
            <Form.Item label="Timezone" style={{ width: 190 }}>
              <Select value={endTz} onChange={setEndTz} options={TIMEZONES} />
            </Form.Item>
          </div>

          <Divider style={{ margin: "8px 0 16px" }}>Artwork</Divider>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <ImageField label="Campaign Banner" value={bannerUrl} onChange={setBannerUrl} folder="campaigns" hint="General/admin-preview banner" />
            <ImageField label="Mobile Banner" value={mobileBannerUrl} onChange={setMobileBannerUrl} folder="campaigns" hint="What the app actually renders — can be a different crop" />
            <ImageField label="Background" value={bgUrl} onChange={setBgUrl} folder="campaigns" hint="Behind the countdown card, optional" />
          </div>

          <Divider style={{ margin: "8px 0 16px" }}>Call to Action &amp; Countdown</Divider>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}><AppInput name="ctaText" label="CTA Text" placeholder="e.g. Notify Me When We Launch" /></div>
            <div style={{ flex: 1 }}>
              <AppInput name="ctaAction" label="CTA Action" placeholder='"NOTIFY_ME", a route like /new-arrivals, or a URL' />
            </div>
          </div>
          <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Form.Item name="countdownEnabled" label="Countdown Enabled" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item name="countdownType" label="Countdown Type" tooltip='AUTO shows "Starts In" before it begins and "Ends In" while active — the normal choice.' style={{ width: 180 }}>
              <Select options={[
                { label: "Auto (recommended)", value: "AUTO" },
                { label: "Always “Starts In”", value: "STARTS_IN" },
                { label: "Always “Ends In”", value: "ENDS_IN" },
              ]} />
            </Form.Item>
            <Form.Item name="theme" label="Theme" style={{ width: 160 }}>
              <Select options={[
                { label: "Festive", value: "festive" },
                { label: "Premium", value: "premium" },
                { label: "Minimal", value: "minimal" },
              ]} />
            </Form.Item>
          </div>

          <Divider style={{ margin: "8px 0 16px" }}>Status &amp; Display</Divider>
          <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Form.Item name="status" label="Status" tooltip="Scheduled/Active/Ended run automatically off the dates above. Draft = still being built, never shown. Paused = force-hidden right now regardless of dates." style={{ width: 160 }}>
              <Select options={[
                { label: "Draft", value: "DRAFT" },
                { label: "Scheduled (auto)", value: "SCHEDULED" },
                { label: "Active (auto)", value: "ACTIVE" },
                { label: "Paused (force off)", value: "PAUSED" },
                { label: "Ended (auto)", value: "ENDED" },
              ]} />
            </Form.Item>
            <Form.Item name="displayPriority" label="Display Priority" tooltip="Higher number wins which BANNER shows if more than one campaign is eligible at once. Has no effect on purchase restriction — every restricting campaign applies regardless of priority." style={{ width: 140 }}>
              <InputNumber min={0} max={999} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="enabled" label="Enabled" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </div>

          <Divider style={{ margin: "8px 0 16px" }}>Purchase Restriction</Divider>
          <div style={{ fontSize: 12, color: "#999", marginBottom: 12, maxWidth: 560 }}>
            Separate from the campaign being shown/active above — a running campaign does NOT block anything by itself.
            Only set this to something other than "No Restriction" when you actually want specific products (or the whole
            store) to be unavailable for purchase for a while.
          </div>
          <Form.Item name="purchaseRestriction" label="Restriction Type">
            <Select value={restriction} onChange={setRestriction} options={[
              { label: "No Restriction (default)", value: "NONE" },
              { label: "Restrict Selected Products", value: "SELECTED_PRODUCTS" },
              { label: "Restrict Selected Categories", value: "SELECTED_CATEGORIES" },
              { label: "Restrict All Products (store-wide)", value: "ALL_PRODUCTS" },
            ]} />
          </Form.Item>

          {restriction === "SELECTED_PRODUCTS" && (
            <Form.Item name="restrictedProductIds" label="Restricted Products">
              <Select mode="multiple" showSearch optionFilterProp="label" placeholder="Search products to restrict…" options={productOptions} />
            </Form.Item>
          )}
          {restriction === "SELECTED_CATEGORIES" && (
            <Form.Item name="restrictedCategoryIds" label="Restricted Categories">
              <Select mode="multiple" showSearch optionFilterProp="label" placeholder="Search categories to restrict…" options={categoryOptions} />
            </Form.Item>
          )}

          {restriction !== "NONE" && (
            <>
              <div style={{ display: "flex", gap: 12 }}>
                <Form.Item name="restrictionStartAt" label="Restriction Start (optional — blank = same as campaign start)" style={{ flex: 1 }}>
                  <DatePicker showTime style={{ width: "100%" }} format="DD MMM YYYY, HH:mm" />
                </Form.Item>
                <Form.Item label="Timezone" style={{ width: 190 }}>
                  <Select value={restrictStartTz} onChange={setRestrictStartTz} options={TIMEZONES} />
                </Form.Item>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <Form.Item name="purchaseUnlockAt" label="Purchase Unlock Time (optional — blank = unlocks only when campaign ends/pauses)" style={{ flex: 1 }}>
                  <DatePicker showTime style={{ width: "100%" }} format="DD MMM YYYY, HH:mm" />
                </Form.Item>
                <Form.Item label="Timezone" style={{ width: 190 }}>
                  <Select value={unlockTz} onChange={setUnlockTz} options={TIMEZONES} />
                </Form.Item>
              </div>
              <AppInput name="restrictionMessage" label="Restriction message (optional)" placeholder='e.g. "Available when the sale starts" — the date is appended automatically' />
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
}
